import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useApi, useQuery } from 'react-ui';
import { ArtistProfileService, type ArtistProfile, pendingAutoFit } from 'ts-core';

/**
 * User-scoped map mode. Active on `/user/:slug` (profile pane open) and
 * `/user/:slug/events` (pane closed). While active it:
 *  - injects a `user:{slug}` query layer so the map + list + search only show
 *    that user's events (scoped server-side via /v3/search `user=`),
 *  - fetches the public profile (`/api/v2/profiles/{slug}`) for the header + pane.
 *
 * Derives everything from the URL because the map runs on the parent app's
 * BrowserRouter (RouterChecker `routerType="browser"`), so the path is visible.
 */
interface UserScopeValue {
    slug: string | null;
    profile: ArtistProfile | null;
    loading: boolean;
    notFound: boolean;
    /**
     * Whether the profile pane is open. Single source of truth shared by the
     * pane (`ProfileDetailPage`) and the "View profile" toggle
     * (`UserScopeHeader`). Initialised from the URL on scope entry, then driven
     * by `openProfilePane`/`closeProfilePane` — including an automatic close
     * when an event is selected (see `UserScopeEventSync`). Stays closed until
     * the user reopens it.
     */
    paneOpen: boolean;
    openProfilePane: () => void;
    closeProfilePane: () => void;
}

const UserScopeContext = createContext<UserScopeValue>({
    slug: null,
    profile: null,
    loading: false,
    notFound: false,
    paneOpen: false,
    openProfilePane: () => {},
    closeProfilePane: () => {},
});

export const useUserScope = (): UserScopeValue => useContext(UserScopeContext);

export const UserScopeProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const location = useLocation();
    const { apiEndpoints, apiKey } = useApi();
    const { addQuery, removeQueryById, has } = useQuery();

    const openMatch = matchPath('/user/:slug', location.pathname);
    const eventsMatch = matchPath('/user/:slug/events', location.pathname);
    const slug =
        (openMatch?.params.slug ?? eventsMatch?.params.slug) ?? null;
    const isEventsPath = Boolean(eventsMatch);

    const service = useMemo(
        () => new ArtistProfileService(apiEndpoints, apiKey),
        [apiEndpoints, apiKey]
    );

    const [profile, setProfile] = useState<ArtistProfile | null>(null);
    const [loading, setLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);

    // Pane open/closed is real state (not URL-derived) so the header toggle and
    // the pane agree, and a programmatic close (e.g. opening an event) sticks.
    // Lazily seeded from the entry URL: open on `/user/:slug`, closed on
    // `/user/:slug/events`.
    const [paneOpen, setPaneOpen] = useState<boolean>(() => Boolean(openMatch));
    const prevSlugRef = useRef<string | null>(slug);
    const openProfilePane = useCallback(() => setPaneOpen(true), []);
    const closeProfilePane = useCallback(() => setPaneOpen(false), []);

    // Re-seed only when the scoped slug actually changes (entering a profile,
    // switching users, or leaving scope) — never on open/close toggles, so a
    // user-initiated close isn't undone by a re-render.
    useEffect(() => {
        if (slug !== prevSlugRef.current) {
            setPaneOpen(slug ? !isEventsPath : false);
            prevSlugRef.current = slug;
        }
    }, [slug, isEventsPath]);

    // Inject / remove the user scope query layer.
    useEffect(() => {
        if (!slug) return;
        const id = `user:${slug}`;
        if (!has(id)) {
            // Arm one-shot map auto-fit so the map fits to this user's events
            // (replaces the bespoke UserScopeMapFit fetch).
            pendingAutoFit.arm();
            addQuery({ id, user: [slug] }, false);
        }
        return () => {
            removeQueryById(id, false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    // Fetch the public profile for the header + pane.
    useEffect(() => {
        let cancelled = false;
        if (!slug) {
            setProfile(null);
            setNotFound(false);
            setLoading(false);
            return;
        }
        setLoading(true);
        setNotFound(false);
        service
            .getArtistBySlug(slug)
            .then((p) => {
                if (cancelled) return;
                if (p) setProfile(p);
                else setNotFound(true);
            })
            .catch(() => {
                if (!cancelled) setNotFound(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [service, slug]);

    const value = useMemo(
        () => ({
            slug,
            profile,
            loading,
            notFound,
            paneOpen,
            openProfilePane,
            closeProfilePane,
        }),
        [slug, profile, loading, notFound, paneOpen, openProfilePane, closeProfilePane]
    );

    return (
        <UserScopeContext.Provider value={value}>
            {children}
        </UserScopeContext.Provider>
    );
};
